<script setup lang="ts">
import { computed } from 'vue'
import { FUND_CONFIG, fmtNav, fmtShortDate, type Fund } from './fundConfig'

const props = defineProps<{
  fund: Fund
  syntheticNav: number | null
  officialNav: number | null
  synthVsOfficialBps: number | null
  isForwardLooking: boolean
  baseNavDate?: string | null
  baseNavStale?: boolean
  // Previous official NAV in the SAME unit as syntheticNav/officialNav —
  // both headline percentages are moves from this one base, so they always
  // reconcile with the synth-vs-official bps delta below.
  baseNav?: number | null
}>()

const cfg = computed(() => FUND_CONFIG[props.fund])
const testId = `nav-comparison-${props.fund.toLowerCase()}`

function fmt(v: number | null | undefined): string {
  return fmtNav(v, props.fund)
}

function fmtBps(v: number | null | undefined): string {
  if (v == null) return '--'
  const sign = v >= 0 ? '+' : ''
  return `${sign}${v.toFixed(1)} bps`
}

function valClass(v: number | null | undefined): string {
  if (v == null || v === 0) return ''
  return v > 0 ? 'positive' : 'negative'
}

function pctFromBase(v: number | null | undefined): number | null {
  if (v == null || props.baseNav == null || props.baseNav === 0) return null
  return (v / props.baseNav - 1) * 100
}

function fmtPct(pct: number): string {
  const sign = pct >= 0 ? '+' : ''
  return `${sign}${pct.toFixed(2)}%`
}

const synthPct = computed(() => pctFromBase(props.syntheticNav))
const officialPct = computed(() => pctFromBase(props.officialNav))
</script>

<template>
  <div class="nav-comparison" :data-testid="testId">
    <div class="section-header">{{ fund }} NAV COMPARISON</div>
    <div class="nav-row">
      <div class="nav-item">
        <span class="nav-label">Synthetic NAV</span>
        <span class="nav-value" :class="{ 'text-amber': isForwardLooking && syntheticNav == null }">
          {{ syntheticNav != null ? fmt(syntheticNav) : (isForwardLooking ? 'Awaiting' : '--') }}
          <span
            v-if="synthPct != null"
            class="nav-pct"
            :class="valClass(synthPct)"
            data-testid="nav-pct-synth"
          >{{ fmtPct(synthPct) }}</span>
        </span>
        <span class="nav-sublabel" v-if="isForwardLooking && baseNavDate">
          Base: {{ fmtShortDate(baseNavDate) }}
        </span>
        <span class="nav-sublabel text-amber" v-if="baseNavStale">Stale base</span>
      </div>
      <div class="nav-item">
        <span class="nav-label">Official NAV</span>
        <span class="nav-value" :class="{ 'text-amber': isForwardLooking && officialNav == null }">
          {{ officialNav != null ? fmt(officialNav) : (isForwardLooking ? 'Awaiting' : '--') }}
          <span
            v-if="officialPct != null"
            class="nav-pct"
            :class="valClass(officialPct)"
            data-testid="nav-pct-official"
          >{{ fmtPct(officialPct) }}</span>
        </span>
      </div>
    </div>
    <div class="delta-cards">
      <div class="delta-card delta-card-primary">
        <div class="delta-label">NAV Delta</div>
        <div class="delta-sublabel">{{ cfg.strikeShort }} Synth vs Official</div>
        <div class="delta-value" :class="valClass(synthVsOfficialBps)" style="font-size: 1.5rem;">
          {{ fmtBps(synthVsOfficialBps) }}
        </div>
      </div>
      <div class="delta-card">
        <div class="delta-label">Synthetic NAV ({{ cfg.strikeTime }})</div>
        <div class="delta-value" style="font-size: 0.9rem; color: #aaa;">
          {{ syntheticNav != null ? fmt(syntheticNav) : '--' }}
        </div>
      </div>
      <div class="delta-card">
        <div class="delta-label">Official NAV (~13:00)</div>
        <div class="delta-value" style="font-size: 0.9rem; color: #aaa;">
          {{ officialNav != null ? fmt(officialNav) : '--' }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.section-header {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #666;
  margin-bottom: 0.5rem;
}
.nav-comparison {
  border: 1px solid #1a2a1a;
  border-radius: 6px;
  padding: 0.75rem;
  background: #0d1a0d;
}
.nav-row { display: flex; gap: 1rem; margin-bottom: 0.5rem; }
.nav-item { flex: 1; display: flex; flex-direction: column; gap: 0.15rem; }
.nav-label { font-size: 9px; text-transform: uppercase; letter-spacing: 2px; color: #666; }
.nav-value { font-family: monospace; font-size: 0.95rem; color: #ccc; }
.nav-pct { font-size: 0.75rem; margin-left: 4px; color: #888; }
.nav-sublabel { display: block; font-size: 0.7rem; color: #888; margin-top: 2px; }
.text-amber { color: #ff9800; }
.delta-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.4rem; }
.delta-card {
  border: 1px solid #222;
  border-radius: 4px;
  padding: 0.4rem 0.5rem;
  background: #0a0a0a;
  text-align: center;
}
.delta-label { font-size: 0.7rem; font-weight: 600; color: #aaa; }
.delta-sublabel {
  font-size: 8px; text-transform: uppercase; letter-spacing: 1px;
  color: #555; margin-bottom: 0.15rem;
}
.delta-value { font-family: monospace; font-size: 0.8rem; }
.positive { color: #4caf50 !important; }
.negative { color: #f44336 !important; }

@media (max-width: 768px) {
  .section-header { font-size: 0.7rem; }
}
</style>
